// Fresh arrivals ("Today's catch"): which products can be logged, the quantity field in the
// product's stock unit, and display. Pure, no Intl.

/** @typedef {import('@popcart/core/v1').Arrival} Arrival */
/** @typedef {{ productId: string; name: string; line: import('@popcart/core/v1').ProductLine; available: number; onHand: number }} StockRow */

export const MAX_PIECES = 10000;
export const MAX_ARRIVAL_GRAMS = 1000000; // 1 tonne in one delivery is already a lot
export const MAX_SOURCE = 80;

/**
 * The shop's Fresh products for the picker, by name.
 * @param {StockRow[]} rows
 */
export function freshProducts(rows) {
  return rows.filter((r) => r.line === 'fresh').sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * The stock unit for a product: target-weight items count grams, everything else pieces
 * (docs/PHASE3-CONTRACT.md "Stock").
 * @param {{ pricing?: string; weight?: { mode: 'band' | 'target' } | null } | null | undefined} p
 * @returns {'piece' | 'gram'}
 */
export function stockUnitOf(p) {
  return p && p.pricing === 'weight' && p.weight && p.weight.mode === 'target' ? 'gram' : 'piece';
}

/**
 * Read the quantity field in the product's unit. Pieces are whole numbers. Grams accept a
 * plain number of grams ("18400", "18,400 g") or kilograms ("18.4 kg").
 * @param {string} input
 * @param {'piece' | 'gram'} unit
 * @returns {{ ok: true; quantity: number } | { ok: false; error: string }}
 */
export function parseArrivalQuantity(input, unit) {
  let s = String(input ?? '').replace(/[\s  ]+/g, '').toLowerCase();
  if (!s) return { ok: false, error: unit === 'gram' ? 'Enter how much arrived (grams or kg).' : 'Enter how many arrived.' };
  if (unit === 'piece') {
    s = s.replace(/(pcs|pc|pieces|piece)$/, '');
    if (/^\d{1,3}(,\d{3})+$/.test(s)) s = s.replace(/,/g, '');
    if (!/^\d+$/.test(s)) return { ok: false, error: 'Whole pieces only.' };
    const n = Number(s);
    if (n < 1) return { ok: false, error: 'At least 1 piece.' };
    if (n > MAX_PIECES) return { ok: false, error: 'That is over 10,000 pieces. Check the number.' };
    return { ok: true, quantity: n };
  }
  let grams;
  if (/kg$/.test(s)) {
    const k = s.slice(0, -2).replace(/,/g, '.');
    if (!/^\d+(\.\d{1,3})?$/.test(k)) return { ok: false, error: 'Write kilograms like 18.4 kg.' };
    grams = Math.round(Number(k) * 1000);
  } else {
    let g = s.replace(/(grams|g)$/, '');
    if (/^\d{1,3}(,\d{3})+$/.test(g)) g = g.replace(/,/g, '');
    if (!/^\d+$/.test(g)) return { ok: false, error: 'Whole grams, or kilograms like 18.4 kg.' };
    grams = Number(g);
  }
  if (grams < 1) return { ok: false, error: 'At least 1 g.' };
  if (grams > MAX_ARRIVAL_GRAMS) return { ok: false, error: 'That is over 1,000 kg. Check the number.' };
  return { ok: true, quantity: grams };
}

/** Optional source ("Navotas fish port"): trimmed, ≤ 80 characters, undefined when blank.
 * @param {string} s
 * @returns {{ ok: true; source: string | undefined } | { ok: false; error: string }}
 */
export function cleanSource(s) {
  const t = String(s ?? '').replace(/\s+/g, ' ').trim();
  if (!t) return { ok: true, source: undefined };
  if (t.length > MAX_SOURCE) return { ok: false, error: `Keep the source under ${MAX_SOURCE} characters.` };
  return { ok: true, source: t };
}

/**
 * "18.4 kg", "850 g", "40 pcs", "1 pc".
 * @param {number} quantity @param {'piece' | 'gram'} unit
 */
export function formatQuantity(quantity, unit) {
  if (!Number.isFinite(quantity)) return '—';
  if (unit === 'piece') return `${quantity} ${quantity === 1 ? 'pc' : 'pcs'}`;
  if (quantity < 1000) return `${Math.round(quantity)} g`;
  const kg = Math.round(quantity / 100) / 10;
  return `${kg % 1 === 0 ? kg.toFixed(0) : kg.toFixed(1)} kg`;
}

/** Local midnight as ISO, for `arrivals?since=`. @param {Date} now */
export function startOfToday(now) {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return d.toISOString();
}

/** Newest first. @param {Arrival[]} xs */
export function sortArrivals(xs) {
  return xs.slice().sort((a, b) => (a.arrivedAt < b.arrivedAt ? 1 : a.arrivedAt > b.arrivedAt ? -1 : 0));
}
